import { computed, ref } from 'vue'
import { useListingsStore } from '@/stores/listings'
import type { Category } from '@/types/api'

/**
 * A listing form's category and optional subcategory. The subcategory select
 * only matters when the chosen category has subcategories; picking another
 * category clears it. Submit `selectedID`: the subcategory if one is picked.
 */
export function useCategoryChoice() {
  const listingsStore = useListingsStore()
  const categoryId = ref('')
  const subcategoryId = ref('')

  const categories = computed(() => listingsStore.categories.filter(c => !c.parentID))
  const subcategories = computed(() => categories.value.find(c => c.id === categoryId.value)?.children ?? [])

  /** Bind the category select to this: changing it resets the subcategory. */
  const category = computed({
    get: () => categoryId.value,
    set: (id: string) => {
      categoryId.value = id
      subcategoryId.value = ''
    },
  })

  const selectedID = computed(() => subcategoryId.value || categoryId.value)

  /** Preselects a listing's category, splitting a subcategory into its parent and itself. */
  function select(current: Pick<Category, 'id' | 'parentID'>) {
    categoryId.value = current.parentID ?? current.id
    subcategoryId.value = current.parentID ? current.id : ''
  }

  return { categories, subcategories, category, subcategoryId, selectedID, select }
}
